import React from "react";

/* ═══════════════════════════════════════════════════════════════
   History, Culture & Everyday — 10 templates
   era_timeline, castle_fortress, ancient_monument, trade_route,
   food_pyramid_diagram, water_cycle_diagram, traffic_light,
   house_blueprint, sports_field, color_spectrum
   ═══════════════════════════════════════════════════════════════ */

/* ─── 1. Era Timeline ─────────────────────────────────────── */
export const EraTimeline: React.FC<Record<string, unknown>> = (props) => {
  const label = (props.label as string) || "Timeline";
  const activeIndex = (props.activeIndex as number) ?? -1;
  const eras = (props.eras as string[]) || ["Ancient", "Classical", "Medieval", "Renaissance", "Modern"];
  return (
    <svg viewBox="0 0 800 600" width="100%" height="100%">
      <rect width="800" height="600" fill="#0d1117" rx="16" />
      {/* Timeline bar */}
      <rect x="80" y="275" width="640" height="8" rx="4" fill="#2c3e50" />
      {/* Era segments */}
      {eras.map((era, i) => {
        const segW = 640 / eras.length;
        const x = 80 + i * segW;
        const colors = ["#e74c3c", "#f39c12", "#2ecc71", "#3498db", "#9b59b6"];
        return (
          <g key={i}>
            <rect x={x} y="275" width={segW} height="8" fill={colors[i % colors.length]}
              opacity={i === activeIndex ? 1 : 0.4} />
            {/* Marker */}
            <circle cx={x + segW / 2} cy="279" r={i === activeIndex ? 16 : 12}
              fill={i === activeIndex ? colors[i % colors.length] : "#1c2833"}
              stroke={colors[i % colors.length]} strokeWidth="2" />
            {/* Event card */}
            <rect x={x + 5} y={i % 2 === 0 ? 160 : 320} width={segW - 10} height="70" rx="8"
              fill={i === activeIndex ? colors[i % colors.length] : "#1c2833"}
              stroke={colors[i % colors.length]} strokeWidth="1.5"
              opacity={i === activeIndex ? 0.9 : 0.5} />
            <text x={x + segW / 2} y={i % 2 === 0 ? 200 : 360} textAnchor="middle"
              fill="#ecf0f1" fontSize="12" fontWeight="600">{era}</text>
            {/* Connector line */}
            <line x1={x + segW / 2} y1={i % 2 === 0 ? 230 : 320}
              x2={x + segW / 2} y2="275" stroke={colors[i % colors.length]}
              strokeWidth="1" strokeDasharray="4,3" opacity="0.5" />
          </g>
        );
      })}
      <text x="400" y="490" textAnchor="middle" fill="#ecf0f1" fontSize="20" fontWeight="600">{label}</text>
    </svg>
  );
};

/* ─── 2. Castle Fortress ──────────────────────────────────── */
export const CastleFortress: React.FC<Record<string, unknown>> = (props) => {
  const label = (props.label as string) || "Castle";
  const highlightTarget = (props.highlightTarget as string) || "";
  return (
    <svg viewBox="0 0 800 600" width="100%" height="100%">
      <defs>
        <linearGradient id="cf-sky" x1="0" y1="0" x2="0" y2="1">
          <stop offset="0%" stopColor="#1a237e" /><stop offset="100%" stopColor="#283593" />
        </linearGradient>
      </defs>
      <rect width="800" height="600" fill="url(#cf-sky)" rx="16" />
      {/* Moon */}
      <circle cx="650" cy="80" r="25" fill="#eceff1" opacity="0.8" />
      {/* Castle body */}
      <rect x="250" y="250" width="300" height="200"
        fill={highlightTarget === "keep" ? "#5d4037" : "#3e2723"} stroke="#4e342e" strokeWidth="2" />
      {/* Towers */}
      {[{ x: 220, id: "left_tower" }, { x: 530, id: "right_tower" }].map((t) => (
        <g key={t.id}>
          <rect x={t.x} y="180" width="50" height="270" fill={highlightTarget === t.id ? "#5d4037" : "#3e2723"}
            stroke="#4e342e" strokeWidth="2" />
          {/* Battlements */}
          {[0, 15, 30].map((dx) => (
            <rect key={dx} x={t.x + dx + 2} y="170" width="10" height="15" fill="#3e2723" stroke="#4e342e" strokeWidth="1" />
          ))}
          {/* Window */}
          <rect x={t.x + 18} y="220" width="14" height="20" rx="7" fill="#1a237e" opacity="0.6" />
        </g>
      ))}
      {/* Center battlements */}
      {Array.from({ length: 10 }, (_, i) => (
        <rect key={i} x={260 + i * 28} y="240" width="14" height="15" fill="#3e2723" stroke="#4e342e" strokeWidth="1" />
      ))}
      {/* Gate */}
      <path d="M370,450 L370,340 Q400,310 430,340 L430,450" fill="#1a237e" opacity="0.5"
        stroke={highlightTarget === "gate" ? "#fff" : "#4e342e"} strokeWidth={highlightTarget === "gate" ? 3 : 2} />
      {/* Ground */}
      <rect x="0" y="450" width="800" height="150" fill="#1b5e20" opacity="0.5" />
      <text x="400" y="540" textAnchor="middle" fill="#ecf0f1" fontSize="20" fontWeight="600">{label}</text>
    </svg>
  );
};

/* ─── 3. Ancient Monument ─────────────────────────────────── */
export const AncientMonument: React.FC<Record<string, unknown>> = (props) => {
  const label = (props.label as string) || "Monument";
  const accentColor = (props.accentColor as string) || "#f9a825";
  return (
    <svg viewBox="0 0 800 600" width="100%" height="100%">
      <defs>
        <linearGradient id="am-sky" x1="0" y1="0" x2="0" y2="1">
          <stop offset="0%" stopColor="#e65100" stopOpacity="0.3" /><stop offset="100%" stopColor="#0d1117" />
        </linearGradient>
      </defs>
      <rect width="800" height="600" fill="url(#am-sky)" rx="16" />
      {/* Pyramid */}
      <polygon points="400,100 200,440 600,440" fill="#8d6e63" stroke="#6d4c41" strokeWidth="2" />
      {/* Stone layers */}
      {[200, 260, 320, 380].map((y, i) => {
        const w = 400 - ((y - 100) / 340) * 400;
        return <line key={i} x1={400 - w / 2} y1={y} x2={400 + w / 2} y2={y} stroke="#6d4c41" strokeWidth="1.5" />;
      })}
      {/* Capstone glow */}
      <polygon points="400,100 385,140 415,140" fill={accentColor} opacity="0.8" />
      <circle cx="400" cy="120" r="30" fill={accentColor} opacity="0.1" />
      {/* Sand ground */}
      <rect x="0" y="440" width="800" height="160" fill="#d4a76a" opacity="0.3" />
      {/* Small pyramids in background */}
      <polygon points="150,440 100,370 200,440" fill="#6d4c41" opacity="0.4" />
      <polygon points="680,440 640,390 720,440" fill="#6d4c41" opacity="0.3" />
      <text x="400" y="540" textAnchor="middle" fill="#ecf0f1" fontSize="20" fontWeight="600">{label}</text>
    </svg>
  );
};

/* ─── 4. Trade Route ──────────────────────────────────────── */
export const TradeRoute: React.FC<Record<string, unknown>> = (props) => {
  const label = (props.label as string) || "Trade Route";
  const flowProgress = (props.flowProgress as number) || 0;
  const stops = (props.stops as string[]) || ["Origin", "Port A", "Market", "Destination"];
  return (
    <svg viewBox="0 0 800 600" width="100%" height="100%">
      <rect width="800" height="600" fill="#0d1117" rx="16" />
      {/* Sea background */}
      <rect x="0" y="200" width="800" height="250" fill="#0d47a1" opacity="0.2" rx="0" />
      {/* Route path */}
      <path d="M100,300 Q250,200 400,280 Q550,360 700,260"
        fill="none" stroke="#f9a825" strokeWidth="3" strokeDasharray="10,6"
        strokeDashoffset={-flowProgress * 100} />
      {/* Stop cities */}
      {[
        { x: 100, y: 300 }, { x: 280, y: 230 }, { x: 500, y: 320 }, { x: 700, y: 260 },
      ].map((s, i) => (
        <g key={i}>
          {/* City icon */}
          <rect x={s.x - 15} y={s.y - 30} width="30" height="25" rx="3" fill="#8d6e63" />
          <polygon points={`${s.x - 18},${s.y - 30} ${s.x},${s.y - 48} ${s.x + 18},${s.y - 30}`} fill="#6d4c41" />
          <circle cx={s.x} cy={s.y} r="6" fill={flowProgress >= i / 3 ? "#f9a825" : "#546e7a"} />
          <text x={s.x} y={s.y + 25} textAnchor="middle" fill="#ecf0f1" fontSize="12">{stops[i]}</text>
        </g>
      ))}
      <text x="400" y="500" textAnchor="middle" fill="#ecf0f1" fontSize="20" fontWeight="600">{label}</text>
    </svg>
  );
};

/* ─── 5. Food Pyramid Diagram ─────────────────────────────── */
export const FoodPyramidDiagram: React.FC<Record<string, unknown>> = (props) => {
  const label = (props.label as string) || "Food Pyramid";
  const activeIndex = (props.activeIndex as number) ?? -1;
  const layers = (props.layers as string[]) || ["Fats & Sweets", "Proteins", "Fruits & Vegs", "Grains"];
  return (
    <svg viewBox="0 0 800 600" width="100%" height="100%">
      <rect width="800" height="600" fill="#0d1117" rx="16" />
      {[
        { y: 100, w: 120, h: 90, c: "#e74c3c" },
        { y: 190, w: 220, h: 90, c: "#f39c12" },
        { y: 280, w: 340, h: 90, c: "#4caf50" },
        { y: 370, w: 460, h: 100, c: "#8d6e63" },
      ].map((l, i) => (
        <g key={i}>
          <polygon points={`${400 - l.w / 2},${l.y + l.h} ${400 - l.w / 2 + 30},${l.y} ${400 + l.w / 2 - 30},${l.y} ${400 + l.w / 2},${l.y + l.h}`}
            fill={i === activeIndex ? l.c : l.c}
            opacity={i === activeIndex ? 1 : 0.45}
            stroke={i === activeIndex ? "#fff" : "#00000030"} strokeWidth={i === activeIndex ? 2 : 1} />
          <text x="400" y={l.y + l.h / 2 + 5} textAnchor="middle" fill="#ecf0f1" fontSize="14" fontWeight="500">
            {layers[i]}
          </text>
        </g>
      ))}
      <text x="400" y="540" textAnchor="middle" fill="#ecf0f1" fontSize="20" fontWeight="600">{label}</text>
    </svg>
  );
};

/* ─── 6. Water Cycle Diagram ──────────────────────────────── */
export const WaterCycleDiagram: React.FC<Record<string, unknown>> = (props) => {
  const label = (props.label as string) || "Water Cycle";
  const activeIndex = (props.activeIndex as number) ?? -1;
  const stages = (props.stages as string[]) || ["Evaporation", "Condensation", "Precipitation", "Collection"];
  return (
    <svg viewBox="0 0 800 600" width="100%" height="100%">
      <defs>
        <linearGradient id="wc-sky" x1="0" y1="0" x2="0" y2="1">
          <stop offset="0%" stopColor="#1565c0" /><stop offset="100%" stopColor="#42a5f5" />
        </linearGradient>
      </defs>
      <rect width="800" height="600" fill="url(#wc-sky)" rx="16" />
      {/* Sun */}
      <circle cx="100" cy="80" r="40" fill="#fff9c4" opacity="0.7" />
      {/* Mountain */}
      <polygon points="500,460 600,260 700,460" fill="#455a64" />
      <polygon points="600,260 585,300 615,300" fill="#eceff1" opacity="0.8" />
      {/* Water body */}
      <rect x="0" y="460" width="500" height="140" fill="#0d47a1" opacity="0.5" rx="0" />
      {/* Evaporation arrows */}
      <g opacity={activeIndex === 0 ? 1 : 0.4}>
        {[200, 300, 400].map((x) => (
          <g key={x}>
            <line x1={x} y1="460" x2={x - 20} y2="300" stroke="#90caf9" strokeWidth="2" strokeDasharray="6,4" />
            <polygon points={`${x - 20},300 ${x - 26},315 ${x - 14},315`} fill="#90caf9" />
          </g>
        ))}
        <text x="300" y="390" textAnchor="middle" fill="#ecf0f1" fontSize="13" fontWeight="600">{stages[0]}</text>
      </g>
      {/* Cloud (condensation) */}
      <g opacity={activeIndex === 1 ? 1 : 0.5}>
        <ellipse cx="350" cy="150" rx="100" ry="50" fill="#eceff1" />
        <ellipse cx="300" cy="140" rx="70" ry="40" fill="#e0e0e0" />
        <ellipse cx="410" cy="140" rx="60" ry="35" fill="#e0e0e0" />
        <text x="350" y="100" textAnchor="middle" fill="#ecf0f1" fontSize="13" fontWeight="600">{stages[1]}</text>
      </g>
      {/* Precipitation */}
      <g opacity={activeIndex === 2 ? 1 : 0.4}>
        {[310, 340, 370, 400].map((x, i) => (
          <line key={i} x1={x} y1={200 + i * 10} x2={x - 5} y2={225 + i * 10}
            stroke="#90caf9" strokeWidth="2.5" strokeLinecap="round" />
        ))}
        <text x="450" y="230" fill="#ecf0f1" fontSize="13" fontWeight="600">{stages[2]}</text>
      </g>
      {/* Collection / runoff */}
      <g opacity={activeIndex === 3 ? 1 : 0.4}>
        <path d="M620,360 Q580,420 500,460" fill="none" stroke="#1565c0" strokeWidth="4" strokeLinecap="round" />
        <text x="620" y="400" fill="#ecf0f1" fontSize="13" fontWeight="600">{stages[3]}</text>
      </g>
      <text x="400" y="570" textAnchor="middle" fill="#ecf0f1" fontSize="20" fontWeight="600">{label}</text>
    </svg>
  );
};

/* ─── 7. Traffic Light ────────────────────────────────────── */
export const TrafficLight: React.FC<Record<string, unknown>> = (props) => {
  const label = (props.label as string) || "Traffic Light";
  const activeIndex = (props.activeIndex as number) ?? 0;
  const meanings = (props.meanings as string[]) || ["Stop", "Caution", "Go"];
  return (
    <svg viewBox="0 0 800 600" width="100%" height="100%">
      <rect width="800" height="600" fill="#0d1117" rx="16" />
      {/* Pole */}
      <rect x="385" y="420" width="30" height="140" fill="#546e7a" />
      {/* Housing */}
      <rect x="340" y="80" width="120" height="340" rx="20" fill="#1c2833" stroke="#546e7a" strokeWidth="3" />
      {/* Lights */}
      {[
        { cy: 160, c: "#e74c3c", i: 0 },
        { cy: 260, c: "#f9a825", i: 1 },
        { cy: 360, c: "#4caf50", i: 2 },
      ].map((light) => (
        <g key={light.i}>
          <circle cx="400" cy={light.cy} r="40"
            fill={light.i === activeIndex ? light.c : "#2c3e50"}
            opacity={light.i === activeIndex ? 1 : 0.3} />
          {light.i === activeIndex && (
            <circle cx="400" cy={light.cy} r="50" fill={light.c} opacity="0.15" />
          )}
        </g>
      ))}
      {/* Meaning labels */}
      {meanings.map((m, i) => (
        <text key={i} x="520" y={165 + i * 100} fill={i === activeIndex ? "#ecf0f1" : "#546e7a"}
          fontSize="14" fontWeight={i === activeIndex ? "700" : "400"}>{m}</text>
      ))}
      <text x="400" y="570" textAnchor="middle" fill="#ecf0f1" fontSize="20" fontWeight="600">{label}</text>
    </svg>
  );
};

/* ─── 8. House Blueprint ──────────────────────────────────── */
export const HouseBlueprint: React.FC<Record<string, unknown>> = (props) => {
  const label = (props.label as string) || "Blueprint";
  const highlightTarget = (props.highlightTarget as string) || "";
  return (
    <svg viewBox="0 0 800 600" width="100%" height="100%">
      <rect width="800" height="600" fill="#0a1628" rx="16" />
      {/* Grid */}
      {Array.from({ length: 16 }, (_, i) => (
        <g key={i}>
          <line x1={80 + i * 42} y1="40" x2={80 + i * 42} y2="540" stroke="#1a3a6a" strokeWidth="0.5" />
          <line x1="80" y1={40 + i * 33} x2="740" y2={40 + i * 33} stroke="#1a3a6a" strokeWidth="0.5" />
        </g>
      ))}
      {/* Rooms */}
      {[
        { x: 150, y: 120, w: 200, h: 160, id: "living", lbl: "Living Room" },
        { x: 350, y: 120, w: 200, h: 160, id: "kitchen", lbl: "Kitchen" },
        { x: 150, y: 280, w: 140, h: 140, id: "bedroom1", lbl: "Bedroom 1" },
        { x: 290, y: 280, w: 140, h: 140, id: "bedroom2", lbl: "Bedroom 2" },
        { x: 430, y: 280, w: 120, h: 140, id: "bath", lbl: "Bathroom" },
      ].map((r) => (
        <g key={r.id}>
          <rect x={r.x} y={r.y} width={r.w} height={r.h}
            fill={highlightTarget === r.id ? "#1565c010" : "none"}
            stroke={highlightTarget === r.id ? "#42a5f5" : "#2196f3"} strokeWidth={highlightTarget === r.id ? 3 : 1.5} />
          <text x={r.x + r.w / 2} y={r.y + r.h / 2 + 4} textAnchor="middle"
            fill={highlightTarget === r.id ? "#42a5f5" : "#64b5f6"} fontSize="12">{r.lbl}</text>
        </g>
      ))}
      {/* Door openings */}
      <rect x="240" y="275" width="20" height="10" fill="#0a1628" />
      <rect x="380" y="275" width="20" height="10" fill="#0a1628" />
      <text x="400" y="500" textAnchor="middle" fill="#42a5f5" fontSize="20" fontWeight="600">{label}</text>
    </svg>
  );
};

/* ─── 9. Sports Field ─────────────────────────────────────── */
export const SportsField: React.FC<Record<string, unknown>> = (props) => {
  const label = (props.label as string) || "Sports Field";
  const highlightTarget = (props.highlightTarget as string) || "";
  return (
    <svg viewBox="0 0 800 600" width="100%" height="100%">
      <rect width="800" height="600" fill="#0d1117" rx="16" />
      {/* Field */}
      <rect x="80" y="80" width="640" height="400" fill="#1b5e20" stroke="#fff" strokeWidth="3" rx="4" />
      {/* Center line */}
      <line x1="400" y1="80" x2="400" y2="480" stroke="#fff" strokeWidth="2" />
      {/* Center circle */}
      <circle cx="400" cy="280" r="60" fill="none" stroke="#fff" strokeWidth="2" />
      <circle cx="400" cy="280" r="5" fill="#fff" />
      {/* Goal areas */}
      <rect x="80" y="200" width="60" height="160" fill="none"
        stroke={highlightTarget === "left_goal" ? "#f9a825" : "#fff"} strokeWidth={highlightTarget === "left_goal" ? 3 : 2} />
      <rect x="660" y="200" width="60" height="160" fill="none"
        stroke={highlightTarget === "right_goal" ? "#f9a825" : "#fff"} strokeWidth={highlightTarget === "right_goal" ? 3 : 2} />
      {/* Penalty areas */}
      <rect x="80" y="160" width="120" height="240" fill="none" stroke="#fff" strokeWidth="1.5" />
      <rect x="600" y="160" width="120" height="240" fill="none" stroke="#fff" strokeWidth="1.5" />
      {/* Corner arcs */}
      {[[83, 83], [717, 83], [83, 477], [717, 477]].map(([cx, cy], i) => (
        <path key={i} d={`M${cx},${cy - 15} A15,15 0 0 ${i < 2 ? 1 : 0} ${cx + (i % 2 === 0 ? 15 : -15)},${cy}`}
          fill="none" stroke="#fff" strokeWidth="1.5" />
      ))}
      <text x="400" y="545" textAnchor="middle" fill="#ecf0f1" fontSize="20" fontWeight="600">{label}</text>
    </svg>
  );
};

/* ─── 10. Color Spectrum ──────────────────────────────────── */
export const ColorSpectrum: React.FC<Record<string, unknown>> = (props) => {
  const label = (props.label as string) || "Color Spectrum";
  const activeIndex = (props.activeIndex as number) ?? -1;
  const colors = ["#e74c3c", "#e67e22", "#f1c40f", "#2ecc71", "#3498db", "#2980b9", "#8e44ad"];
  const names = (props.colorNames as string[]) || ["Red", "Orange", "Yellow", "Green", "Blue", "Indigo", "Violet"];
  return (
    <svg viewBox="0 0 800 600" width="100%" height="100%">
      <rect width="800" height="600" fill="#0d1117" rx="16" />
      {/* Prism */}
      <polygon points="200,150 150,400 250,400" fill="#1c2833" stroke="#546e7a" strokeWidth="2" />
      {/* White light entering */}
      <line x1="50" y1="270" x2="180" y2="270" stroke="#ecf0f1" strokeWidth="6" />
      {/* Rainbow bands fanning out */}
      {colors.map((c, i) => {
        const endY = 140 + i * 48;
        return (
          <g key={i}>
            <line x1="230" y1={250 + i * 5} x2="700" y2={endY}
              stroke={c} strokeWidth={i === activeIndex ? 10 : 6}
              opacity={i === activeIndex ? 1 : 0.6} />
            <text x="720" y={endY + 5} fill={i === activeIndex ? "#ecf0f1" : "#7f8c8d"} fontSize="12">
              {names[i]}
            </text>
          </g>
        );
      })}
      {/* Wavelength scale */}
      <text x="400" y="480" textAnchor="middle" fill="#bdc3c7" fontSize="12">380nm ←→ 700nm</text>
      <text x="400" y="540" textAnchor="middle" fill="#ecf0f1" fontSize="20" fontWeight="600">{label}</text>
    </svg>
  );
};
